import PropTypes from 'prop-types';
import React, { useState } from 'react';
import UI from '../ui';
import Utils from '../utils';
import LogoTypeEnum from './LogoType.enum';
import Preview from './Preview.component';
import PreviewModal from './PreviewModal.component';
import PreviewMode from './PreviewMode.enum';

const { WrappedButton, WrappedButtonType } = UI;
const { withLayoutAndModalContexts, Session } = Utils;
const previewClassMap = {
	[PreviewMode.Mobile]: 'branded-tracking-mobile-view',
	[PreviewMode.Desktop]: 'branded-tracking-desktop-view',
	[PreviewMode.Tablet]: 'branded-tracking-tablet-view',
	[PreviewMode.DesktopModal]: 'branded-tracking-desktop-view',
	[PreviewMode.TabletModal]: 'branded-tracking-tablet-view',
};

const getModalTitle = newMode => {
	if (newMode === PreviewMode.DesktopModal) {
		return 'Desktop Preview';
	} else if (newMode === PreviewMode.TabletModal) {
		return 'Tablet Preview';
	} else {
		return '';
	}
};
const transformConfigForPreview = ({
	brandedTrackingConfig,
	notificationConfig,
}) => ({
	source: 'branded-tracking-preview',
	notification_setting: {
		sms_enabled: notificationConfig.AllowSMSOptIn, // WEBAPPS-29567, showing SMS Opt in in preview
	},
	branding: {
		layout_key: 'layout01',
		theme_key:
			brandedTrackingConfig.Theme && brandedTrackingConfig.Theme.toLowerCase(),
		store_name: brandedTrackingConfig.BrandName,
		store_url: brandedTrackingConfig.LogoTargetUrl,
		logo_type:
			brandedTrackingConfig.LogoType &&
			brandedTrackingConfig.LogoType.toLowerCase(),
		logo_url: brandedTrackingConfig.LogoUrl,
		email: brandedTrackingConfig.Email,
		phone: brandedTrackingConfig.Phone,
		return_policy: brandedTrackingConfig.ReturnPolicy,
		menu_links:
			brandedTrackingConfig.MenuLinks &&
			brandedTrackingConfig.MenuLinks.map(c => ({
				url: c.Url,
				text: c.Text,
			})),
		social_media:
			brandedTrackingConfig.SocialMedia &&
			brandedTrackingConfig.SocialMedia.map(c => ({
				type: c.Type.toLowerCase(),
				url: c.Url,
			})),
		store_ids: brandedTrackingConfig.storeIds,
		toggles: {
			show_logo: brandedTrackingConfig.LogoType === LogoTypeEnum.Image,
			show_custom_colors: false,
			show_social_media:
				brandedTrackingConfig.SocialMedia &&
				brandedTrackingConfig.SocialMedia.filter(c => !!c.Url).length > 0,
			show_store_url: !!brandedTrackingConfig.LogoTargetUrl,
			show_menu_links:
				brandedTrackingConfig.MenuLinks &&
				brandedTrackingConfig.MenuLinks.filter(c => !!c.Url).length > 0,
			show_return_policy: !!brandedTrackingConfig.ReturnPolicy,
			show_email: !!brandedTrackingConfig.Email,
			show_phone: !!brandedTrackingConfig.Phone,
			show_shipment_details: true,
			show_item_images: true,
			show_shipment_prices: true,
			show_item_options: true,
		},
	},
});

const Viewport = props => {
	const [activeMode, setActiveMode] = useState(PreviewMode.Mobile);
	const { brandedTrackingConfig, notificationConfig } = props;
	const previewConfig = transformConfigForPreview({
		brandedTrackingConfig,
		notificationConfig,
	});
	const settings = Session.getSettings();
	const url = settings.TrackShipmentPreviewUrl || '';

	const onTabClick = newMode => {
		const showPopup =
			newMode === PreviewMode.DesktopModal ||
			newMode === PreviewMode.TabletModal;
		const modeToShow = showPopup ? PreviewMode.Mobile : newMode;

		setActiveMode(modeToShow);

		if (showPopup) {
			const { showModal } = props.modalContext;

			showModal({
				component: PreviewModal,
				data: {
					previewConfig,
					url,
					baseClass: previewClassMap[newMode],
					modalTitle: getModalTitle(newMode),
				},
			});
		}
	};

	return (
		<div className="branded-tracking-viewport">
			<div className="viewport-panel">
				<div className="branded-tracking-viewport-toggle">
					<div className="tabs-container">
						<WrappedButton
							buttonType={WrappedButtonType.Secondary}
							className={`tab-button mobile ${
								activeMode === PreviewMode.Mobile ? ' active' : ''
							}`}
							label="Mobile"
							onClick={() => onTabClick(PreviewMode.Mobile)}
							dataqa="branded-tracking-mobile"
						/>
						<WrappedButton
							buttonType={WrappedButtonType.Secondary}
							className={`preview-section tab-button desktop ${
								activeMode === PreviewMode.Desktop ? ' active' : ''
							}`}
							label="Desktop"
							onClick={() => onTabClick(PreviewMode.Desktop)}
							dataqa="branded-tracking-desktop"
						/>

						<WrappedButton
							buttonType={WrappedButtonType.Secondary}
							className={`preview-section tab-button tablet ${
								activeMode === PreviewMode.Tablet ? ' active' : ''
							}`}
							label="Tablet"
							onClick={() => onTabClick(PreviewMode.Tablet)}
							dataqa="branded-tracking-tablet"
						/>
						<WrappedButton
							buttonType={WrappedButtonType.Secondary}
							className={`preview-modal tab-button desktop ${
								activeMode === PreviewMode.DesktopModal ? ' active' : ''
							}`}
							label="Desktop"
							onClick={() => onTabClick(PreviewMode.DesktopModal)}
							dataqa="branded-tracking-desktop-modal"
						/>
						<WrappedButton
							buttonType={WrappedButtonType.Secondary}
							className={`preview-modal tab-button tablet ${
								activeMode === PreviewMode.TabletModal ? ' active' : ''
							}`}
							label="Tablet"
							onClick={() => onTabClick(PreviewMode.TabletModal)}
							dataqa="branded-tracking-tablet-modal"
						/>
					</div>
				</div>
				<Preview
					data={previewConfig}
					url={url}
					className={previewClassMap[activeMode]}
				/>
			</div>
		</div>
	);
};

Viewport.propTypes = {
	brandedTrackingConfig: PropTypes.object.isRequired,
	notificationConfig: PropTypes.object.isRequired,
};

export default withLayoutAndModalContexts(Viewport);
